import { useEffect, useState } from "react"
import { useAtom } from "jotai"
import { useParams, useNavigate, Link } from "react-router-dom"
import api from "../../services/api"
import AdminLayout from "../../Components/Layout/AdminLayout"
import Button from "../../Components/Button"
import OrganizationFormModal from "../../Components/Modals/OrganizationFormModal"
import DeleteOrganizationModal from "../../Components/Modals/DeleteOrganizationModal"
import AssignOrgAdminModal from "../../Components/Modals/AssignOrgAdminModal"
import UserFormModal from "../../Components/Modals/UserFormModal"
import { userAtom } from "../../state/index"

const AdminOrganizationDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [user] = useAtom(userAtom)
  const isSuperAdmin = !!(user?.is_admin && user?.is_super_admin)
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [togglingUserId, setTogglingUserId] = useState(null)
  const [togglingUser, setTogglingUser] = useState(null)
  const [editUserModalOpen, setEditUserModalOpen] = useState(null)
  const [assignAdminTarget, setAssignAdminTarget] = useState(null)

  useEffect(() => {
    loadOrganization()
  }, [id])

  const loadOrganization = () => {
    setLoading(true)
    api.getAdminOrganization(id)
      .then((response) => {
        setOrganization(response.data)
        setLoading(false)
      })
      .catch((error) => {
        console.error('Failed to load organization:', error)
        setLoading(false)
      })
  }

  const handleEdit = () => {
    setEditModalOpen(true)
  }

  const handleEditSuccess = () => {
    setEditModalOpen(false)
    loadOrganization()
  }

  const handleDelete = () => {
    setDeleteModalOpen(true)
  }

  const handleDeleteSuccess = () => {
    navigate('/admin/organizations')
  }

  const handleToggleAdmin = (orgUser) => {
    if (!orgUser.is_admin) {
      // Enrich the user object with org context so the modal can pre-select it
      setAssignAdminTarget({ ...orgUser, organization: organization })
    } else {
      setTogglingUserId(orgUser.id)
      api.adminUpdateUser(orgUser.id, { is_admin: false })
        .then(() => loadOrganization())
        .catch((error) => console.error('Failed to update user admin status:', error))
        .finally(() => setTogglingUserId(null))
    }
  }

  const handleToggleEditUser = (user) => {
    setEditUserModalOpen(true)
    setTogglingUser(user)
  }

  const handleEditUserSuccess = () => {
    setEditUserModalOpen(false)
    loadOrganization()
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center text-gray-600">Loading...</div>
        </div>
      </AdminLayout>
    )
  }

  if (!organization) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center text-gray-600">Organization not found</div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Link to="/admin/organizations" className="text-sm text-brandSecondary hover:underline mb-2 inline-block">
              <i className="fa fa-arrow-left mr-2"></i>
              Back to Organizations
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">{organization.name}</h1>
            <p className="text-sm text-gray-600 mt-1">
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">{organization.slug}</code>
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleEdit} variant="secondary-outline">
              <i className="fa fa-edit mr-2"></i>
              Edit
            </Button>
            <Button onClick={handleDelete} variant="secondary-outline">
              <i className="fa fa-trash mr-2"></i>
              Delete
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'users'
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Users ({organization.users_count || 0})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Organization Information</h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{organization.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Slug</dt>
                  <dd className="mt-1">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">{organization.slug}</code>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Users</dt>
                  <dd className="mt-1 text-sm text-gray-900">{organization.users_count || 0}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {organization.created_at ? new Date(organization.created_at).toLocaleDateString() : 'N/A'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
              <div className="flex gap-4">
                <Link
                  to={`/admin/${organization.slug}/users`}
                  className="inline-flex items-center gap-2 bg-brand hover:opacity-90 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition-all duration-200"
                >
                  <i className="fa fa-users"></i>
                  View Users
                </Link>
                <Link
                  to={`/admin/${organization.slug}/plans`}
                  className="inline-flex items-center gap-2 bg-brandSecondary hover:opacity-90 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition-all duration-200"
                >
                  <i className="fa fa-clipboard-list"></i>
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Users in this Organization</h2>
            {organization.users && organization.users.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      {isSuperAdmin && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {organization.users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.pivot?.role === 'admin' ? 'Org Admin' : 'Member'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.is_admin ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-brand bg-opacity-10 text-brand">
                              <i className="fa fa-check mr-1"></i>
                              Admin
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        {isSuperAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <button
                              onClick={() => handleToggleAdmin(user)}
                              disabled={togglingUserId === user.id}
                              className={`text-xs font-medium px-3 py-1 rounded-md border transition-colors ${
                                user.is_admin
                                  ? 'border-red-300 text-red-600 hover:bg-red-50'
                                  : 'border-brand text-brand hover:bg-brand hover:bg-opacity-10'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {togglingUserId === user.id
                                ? '...'
                                : user.is_admin
                                  ? 'Remove Admin'
                                  : 'Make Admin'}
                            </button>
                            <button
                              onClick={() => {
                                handleToggleEditUser(user)
                              }}
                              disabled={togglingUserId === user.id || togglingUser === user}
                              className={'text-xs font-medium px-3 py-1 rounded-md border transition-colors border-gifts text-gifts hover:bg-gifts hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed'}
                            >
                              Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No users in this organization</p>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <OrganizationFormModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSuccess={handleEditSuccess}
        organization={organization}
      />
      <DeleteOrganizationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
        organization={organization}
      />
      <AssignOrgAdminModal
        isOpen={!!assignAdminTarget}
        onClose={() => setAssignAdminTarget(null)}
        onSuccess={() => {
          setAssignAdminTarget(null)
          loadOrganization()
        }}
        user={assignAdminTarget}
      />

    </AdminLayout>
  )
}

export default AdminOrganizationDetail
